import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import { Pen, PencilOff, FileText, Zap, Crown, AlertTriangle, CheckCircle, Bot, Newspaper, ArrowLeft, TrendingUp, Globe, Gamepad2, Coffee, Shield, Smartphone, X } from 'lucide-react';
import axios from "./AxiosInstance.jsx";
import Boutton from "./Button.jsx";

const Decret = memo(({
    actions,
    setGameState,
    gameState,
    setActions,
    setMessage,
    message,
    setLoading,
    setShowNewspaperPopup,
    setPreviousArticles,
    onClose,
    setGuerres,
    setChatStatus,
    isSimulation = false,
    onSimulationComplete = null,
    triggerReaction,
    isTalking,
    transitionsEnabled,
    isMulti = false,
    submitMultiTurn = null,
}) => {
    const [currentPhase, setCurrentPhase] = useState('signing'); // signing, summary, journal, complete
    const [selectedDecrees, setSelectedDecrees] = useState([]);
    const [signingDecree, setSigningDecree] = useState(null);
    const [signatureProgress, setSignatureProgress] = useState(0);
    const [decrees, setDecrees] = useState([]);
    const [showStamp, setShowStamp] = useState(false);
    const [showStampBan, setShowStampBan] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [barre, setBarre] = useState(false);
    const [numActions, setNumActions] = useState(0);
    const [selectedActions, setSelectedActions] = useState([]);
    const [validationStatus, setValidationStatus] = useState(""); // New state for validation status
    const [isProcessingNextStep, setIsProcessingNextStep] = useState(false); // Flag pour éviter les appels multiples
    const isValidating = useRef(false); // Ref au lieu de State pour une mise à jour synchrone

    const leaderName = gameState?.joueur?.leader || "Dirigeant";
    const countryCode = gameState?.joueur?.name || "XX";
    const nbActions = actions ? actions.length : 0;

    const pollTurnStatus = useCallback(async (taskId, apiUrl, token, attempt = 0) => {
        try {
            const res = await fetch(`${apiUrl}/turn_status/${taskId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            const text = await res.text();

            let data;
            try {
                data = JSON.parse(text);
            } catch (e) {
                throw new Error("Réponse invalide du serveur (pas du JSON) :\n" + text);
            }

            if (data.status === 'done') {
                setGameState(data.game_state);
                setGuerres(data.guerres);

                const articlesResponse = await axios.get("/articles");
                setPreviousArticles(articlesResponse.data);
                setShowNewspaperPopup(true);

                setSelectedActions([]);
                setActions([]);
                setMessage("");
                setChatStatus("Tour terminé ! +2 ans");
                setValidationStatus("Validation terminée !");
                setLoading(false);
                isValidating.current = false; // Réinitialiser le flag
            } else if (data.status === 'error') {
                setChatStatus("Erreur : " + data.error);
                setValidationStatus("Erreur : " + data.error);
                setLoading(false);
                isValidating.current = false; // Réinitialiser le flag
            } else {
                if (data.log && data.log.length > 0) {
                    const last = data.log.at(-1);
                    setChatStatus(last);
                    setValidationStatus(last);
                }
                // Backoff exponentiel : 2s → 3s → 4.5s → 6.75s → max 10s
                const delay = Math.min(2000 * Math.pow(1.5, attempt), 10000);
                setTimeout(() => pollTurnStatus(taskId, apiUrl, token, attempt + 1), delay);
            }
        } catch (error) {
            console.error("Erreur de suivi du tour :", error);
            setChatStatus("Erreur pendant le suivi du tour.");
            setValidationStatus("Erreur pendant le suivi du tour.");
            setLoading(false);
            isValidating.current = false; // Réinitialiser le flag
        }
    }, [setGameState, setGuerres, setPreviousArticles, setShowNewspaperPopup, setSelectedActions, setActions, setMessage, setChatStatus, setLoading]);

    const handleValidateTurn = useCallback(async () => {
        if (isValidating.current) return;

        if (isSimulation) {
            isValidating.current = true;
            setLoading(true);
            setValidationStatus("Simulation de l'analyse stratégique...");
            setTimeout(() => {
                setLoading(false);
                setValidationStatus("Analyse terminée !");
                isValidating.current = false;
                if (onSimulationComplete) onSimulationComplete();
            }, 10000);
            return;
        }

        // Mode multijoueur : soumettre via submitMultiTurn au lieu de /validate_turn
        if (isMulti && submitMultiTurn) {
            isValidating.current = true;
            setLoading(true);
            setValidationStatus("Soumission de vos actions...");

            try {
                await submitMultiTurn(selectedActions);
                setValidationStatus("Actions soumises ! En attente des autres joueurs...");
                setSelectedActions([]);
                setActions([]);
                setMessage("");
                setChatStatus("Actions soumises ! En attente des autres joueurs...");
            } catch (error) {
                console.error("Erreur soumission multi", error);
                setChatStatus(typeof error === 'string' ? error : "Erreur lors de la soumission.");
                setValidationStatus(typeof error === 'string' ? error : "Erreur lors de la soumission.");
            } finally {
                setLoading(false);
                isValidating.current = false;
            }
            return;
        }

        isValidating.current = true;
        setLoading(true);
        setValidationStatus("Démarrage de la validation du tour...");

        try {
            const token = localStorage.getItem('jwtToken');
            const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:5000';

            const response = await fetch(`${apiUrl}/validate_turn`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ actions: selectedActions })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const taskId = data.task_id;
            if (!taskId) {
                throw new Error("Aucune tâche retournée.");
            }

            setValidationStatus("Tour en cours...");
            pollTurnStatus(taskId, apiUrl, token); // NE PAS arrêter loading ici
        } catch (error) {
            console.error("Erreur validation tour", error);
            setChatStatus("Erreur de communication avec le serveur.");
            setValidationStatus("Erreur de communication avec le serveur.");
            setLoading(false); // uniquement ici si échec immédiat
            isValidating.current = false; // Réinitialiser le flag
        }
    }, [currentPhase, setLoading, onSimulationComplete, pollTurnStatus, setGameState, setGuerres, setPreviousArticles, setShowNewspaperPopup, setSelectedActions, setActions, setMessage, setChatStatus, isSimulation, selectedActions, isMulti, submitMultiTurn]);

    const handleSignatureNextStep = useCallback(() => {
        const currentIndex = selectedDecrees.findIndex(d => d.id === signingDecree?.id);


        const nextIndex = currentIndex + 1;
        if (nextIndex < selectedDecrees.length) {
            setSigningDecree(selectedDecrees[nextIndex]);
            setSignatureProgress(0);
            setNumActions(nextIndex + 1);
            setIsAnimating(false);
            setShowStamp(false);
            setBarre(false);
            setShowStamp(false);
            setShowStampBan(false);
            setIsProcessingNextStep(false); // Réinitialiser le flag pour le prochain décret
        } else {
            // Directement valider le tour
            setIsAnimating(false);
            setShowStamp(false);
            handleValidateTurn();
        }
    }, [selectedDecrees, signingDecree, handleValidateTurn]);

    const startSignature = useCallback(() => {
        const action = {
            action: signingDecree.title,
            description: signingDecree.description,
            cible: signingDecree.category
        };
        setSelectedActions(prev => [...prev, action]);
        setIsAnimating(true);
        setSignatureProgress(0);
        setShowStamp(false);
    }, [signingDecree]);

    const resetSignature = useCallback(() => {
        if (currentPhase === 'summary') {
            onClose();
        } else {
            setBarre(true);
            setShowStampBan(true);
        }
    }, [currentPhase, onClose]);
    // Animation de la signature lettre par lettre
    useEffect(() => {
        if (isAnimating) {
            const interval = setInterval(() => {
                setSignatureProgress(prev => {
                    if (prev >= leaderName.length) {
                        clearInterval(interval);
                        setTimeout(() => setShowStamp(true), 200);
                        setTimeout(() => {
                            // En mode simulation avec transitions IA, on laisse l'IA finir de parler 
                            // avant de passer à la suite (géré par un autre useEffect)
                            if (!isProcessingNextStep && !(isSimulation && transitionsEnabled)) {
                                setIsProcessingNextStep(true);
                                handleSignatureNextStep();
                            }
                        }, 1000);
                        return prev;
                    }
                    return prev + 1;
                });
            }, 60); // Vitesse d'écriture
            return () => clearInterval(interval);
        }
    }, [isAnimating, leaderName, isProcessingNextStep, isSimulation, transitionsEnabled, handleSignatureNextStep]);

    // Animation du deceret barré et tampon
    useEffect(() => {
        if (barre) {

            const timeout = setTimeout(() => {
                if (!isProcessingNextStep) {
                    setIsProcessingNextStep(true);
                    handleSignatureNextStep();
                }
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [barre, isProcessingNextStep, handleSignatureNextStep]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && !isAnimating && !barre) {

                if (currentPhase === 'summary') {
                    handleValidateTurn();
                } else if (currentPhase === 'signing') {
                    startSignature();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [signingDecree, isAnimating, barre, currentPhase]);




    useEffect(() => {
        if (actions && actions.length > 0 && decrees.length === 0) {

            const mappedDecrees = actions.map((action, idx) => ({
                id: idx + 1,
                title: action.action,
                category: action.cible || '',
                icon: <Zap className="w-6 h-6" />,
                description: action.description,
                consequences: [],
                urgency: '',
                color: 'from-blue-500 to-purple-600',
            }));

            setDecrees(mappedDecrees);
            setSelectedDecrees(mappedDecrees);
            setSigningDecree(mappedDecrees[0]);
            setNumActions(1);
        }
    }, [actions]);

    // -- Synchronisation TTS et Transitions (Simulation) --
    const hasTriggeredRef = useRef(null);
    const wasTalkingRef = useRef(false);
    const safetyTimerRef = useRef(null);



    // 1. Déclencher le TTS quand le décret change
    useEffect(() => {
        if (isSimulation && transitionsEnabled && signingDecree && triggerReaction && currentPhase === 'signing') {
            if (hasTriggeredRef.current !== signingDecree.id) {
                hasTriggeredRef.current = signingDecree.id;
                triggerReaction(signingDecree.description, 'tts');
            }
        }
    }, [isSimulation, transitionsEnabled, signingDecree, triggerReaction, currentPhase]);

    // 2. Gérer isTalking et le passage au suivant
    useEffect(() => {
        if (!isSimulation || !transitionsEnabled || currentPhase !== 'signing') return;

        if (isTalking) {
            wasTalkingRef.current = true;
            if (!isAnimating && !barre) {
                startSignature();
            }
        } else if (wasTalkingRef.current && !isTalking) {
            wasTalkingRef.current = false;
            setTimeout(() => {
                if (!isProcessingNextStep) {
                    setIsProcessingNextStep(true);
                    handleSignatureNextStep();
                }
            }, 1000);
        }
    }, [isTalking, isSimulation, transitionsEnabled, currentPhase, isAnimating, barre, isProcessingNextStep, handleSignatureNextStep, startSignature]);

    // 3. Timeout de secours (si pas de parole détectée)
    useEffect(() => {
        if (!isSimulation || !transitionsEnabled || currentPhase !== 'signing' || isTalking || isProcessingNextStep) {
            if (safetyTimerRef.current) {
                clearTimeout(safetyTimerRef.current);
                safetyTimerRef.current = null;
            }
            return;
        }

        safetyTimerRef.current = setTimeout(() => {
            console.warn("🚨 [TTS] Safety timeout reached! No speech or transition. Forcing next step.");
            if (!isProcessingNextStep) {
                setIsProcessingNextStep(true);
                handleSignatureNextStep();
            }
        }, 12000);

        return () => {
            if (safetyTimerRef.current) {
                clearTimeout(safetyTimerRef.current);
            }
        };
    }, [isSimulation, transitionsEnabled, currentPhase, isTalking, isProcessingNextStep, signingDecree?.id, handleSignatureNextStep]);

    return (
        <div className="popup-overlay">
            <div className="popup-content custom-scrollbar">
                <div className="min-h-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
                    {/* Header */}
                    {!isSimulation && (
                        <div className="relative z-10 p-4 border-b border-slate-700/50 backdrop-blur-sm bg-slate-900/80">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                                        <FileText className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                                            Chambre des Décrets
                                        </h1>
                                        <p className="text-sm text-slate-400">Session stratégique {gameState?.year}-{gameState?.year + 2}</p>
                                    </div>
                                </div>
                                <div className="text-right text-sm text-slate-400">
                                    <div>Mandat en cours</div>
                                    <div className="text-blue-400 font-semibold">{gameState?.joueur?.name}</div>
                                </div>
                            </div>
                        </div>
                    )}


                    {currentPhase === 'signing' && signingDecree && (
                        <div className="flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 p-8"
                            style={{ minHeight: '80vh' }}>
                            {/* Parchemin */}
                            <div className="relative bg-gradient-to-b from-amber-50 to-amber-100 p-12 rounded-lg shadow-2xl border-4 border-amber-200 max-w-screen-lg w-full">
                                {/* Texture du papier */}
                                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_1px_1px,rgba(139,69,19,0.3)_1px,transparent_0)] rounded-lg"></div>

                                {/* En-tête officiel */}
                                <div className="text-center mb-8 relative z-10">
                                    <h1 className="text-3xl font-bold text-slate-800 mb-2 font-serif tracking-wide">
                                        DÉCRET
                                    </h1>
                                    <div className="w-32 h-1 bg-gradient-to-r from-blue-600 to-purple-600 mx-auto mb-4"></div>
                                    <p className="text-slate-600 font-medium">
                                        {countryCode} • Document Officiel
                                    </p>
                                </div>

                                {/* Contenu du décret */}
                                <div className="mb-6 text-slate-700 leading-relaxed relative z-10">
                                    <p className={`text-base font-serif mb-4 duration-500 ${barre
                                        ? ' line-through'
                                        : ''
                                        }`}>
                                        Par la présente, {leaderName} dirigeant de la nation {gameState?.joueur?.short_name}, déclarons et ordonnons l'application immédiate des mesures stratégiques énoncées dans le présent décret.
                                    </p>
                                    <p className={`text-lg mb-12  ${barre
                                        ? ' line-through'
                                        : ''
                                        }`}>
                                        {signingDecree?.description}
                                    </p>
                                </div>

                                {/* Zone de signature */}
                                <div className="border-t-2 border-slate-300 pt-8 relative z-10">
                                    <div className="flex justify-between items-end">
                                        <div className="flex-1">
                                            <p className="text-sm text-slate-500 mb-2">Signature {leaderName} :</p>
                                            <div className="relative h-16 border-b border-slate-300">
                                                {/* Ligne de signature avec animation d'écriture */}
                                                <div
                                                    className="absolute bottom-2 left-0 text-4xl font-bold text-black transition-all duration-300"
                                                    style={{
                                                        fontFamily: 'Brush Script MT, cursive',
                                                        transform: 'rotate(-2deg)',
                                                        filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.3))'
                                                    }}
                                                >
                                                    {leaderName.slice(0, signatureProgress)}
                                                    {isAnimating && signatureProgress < leaderName.length && (
                                                        <span className="animate-pulse text-blue-600">|</span>
                                                    )}
                                                </div>

                                                {/* Effet d'encre qui coule */}
                                                {isAnimating && (
                                                    <div
                                                        className="absolute bottom-1 opacity-30 w-1 h-1 bg-blue-800 rounded-full animate-pulse"
                                                        style={{
                                                            left: `${Math.min(signatureProgress * 12, 200)}px`,
                                                            animationDuration: '0.5s'
                                                        }}
                                                    ></div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tampon officiel validé*/}
                                        <div className="ml-8 relative">
                                            <div
                                                className={`w-24 h-24 rounded-full border-4 border-green-600 flex items-center justify-center relative transition-all duration-500 ${showStamp
                                                    ? 'opacity-100 scale-100 rotate-6'
                                                    : 'opacity-0 scale-50 rotate-45'
                                                    }`}
                                                style={{
                                                    background: 'radial-gradient(circle, rgba(38,220,38,0.8) 0%, rgba(38,220,38,0.4) 100%)',
                                                    filter: showStamp ? 'drop-shadow(2px 2px 4px rgba(0,0,0,0.4))' : 'none'
                                                }}
                                            >
                                                <div className="text-center">
                                                    <div className="text-green-900 font-bold text-lg">{gameState?.joueur?.short_name}</div>
                                                    <div className="text-green-800 font-black text-xs">VALIDÉ</div>
                                                </div>

                                                {/* Effet d'encre du tampon */}
                                                {showStamp && (
                                                    <div className="absolute inset-0 rounded-full bg-green-600 opacity-20 animate-ping"></div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Tampon officiel abandon*/}
                                        <div className="ml-8 relative">
                                            <div
                                                className={`w-24 h-24 rounded-full border-4 border-red-600 flex items-center justify-center relative transition-all duration-500 ${showStampBan
                                                    ? 'opacity-100 scale-100 rotate-6'
                                                    : 'opacity-0 scale-50 rotate-45'
                                                    }`}
                                                style={{
                                                    background: 'radial-gradient(circle, rgba(220,38,38,0.8) 0%, rgba(220,38,38,0.4) 100%)',
                                                    filter: showStampBan ? 'drop-shadow(2px 2px 4px rgba(0,0,0,0.4))' : 'none'
                                                }}
                                            >
                                                <div className="text-center">
                                                    <div className="text-red-900 font-bold text-lg">{gameState?.joueur?.short_name}</div>
                                                    <div className="text-red-800 font-black text-xs">ABANDON</div>
                                                </div>

                                                {/* Effet d'encre du tampon */}
                                                {showStampBan && (
                                                    <div className="absolute inset-0 rounded-full bg-red-600 opacity-20 animate-ping"></div>
                                                )}
                                            </div>
                                        </div>

                                    </div>

                                    {/* Date */}
                                    <div className="mt-6 text-right text-sm text-slate-500">
                                        Signé le {new Date().toLocaleDateString('fr-FR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </div>
                                </div>

                                {/* Sceau en filigrane */}
                                <div className="absolute top-4 right-4 w-20 h-20 border-2 border-black rounded-full flex items-center justify-center opacity-60">
                                    <div className="text-xs text-center text-black font-bold">
                                        <div>SCEAU</div>
                                        <div>OFFICIEL</div>
                                        <div>{numActions}/{nbActions}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Boutons de contrôle */}
                            <div className="mt-8 flex gap-4">
                                <Boutton
                                    text={isAnimating ? 'En cours...' : 'Signer'}
                                    onClick={startSignature}
                                    text_after="Signer"
                                    disabled={isAnimating || barre}
                                    delay={0}
                                    variant="green"
                                />
                                <Boutton
                                    text="Abandonner"
                                    onClick={resetSignature}
                                    text_after="Abandonner"
                                    disabled={isAnimating || barre}
                                    delay={0}
                                    variant="red"
                                />
                            </div>

                            {/* Indicateur de progression */}
                            {isAnimating && (
                                <div className="mt-4 w-64 bg-slate-700 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
                                        style={{ width: `${(signatureProgress / leaderName.length) * 100}%` }}
                                    ></div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* AI Summary Phase */}
                    {currentPhase === 'summary' && (
                        <div className="flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 p-8"
                            style={{ minHeight: '80vh' }}>
                            <div className="bg-slate-800/80 backdrop-blur-sm rounded-2xl p-8 max-w-screen-xl w-full border border-slate-700/50 shadow-2xl">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg">
                                        <Bot className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-white">Votre 1er IA Ministre</h2>
                                        <p className="text-slate-400 text-sm">Analyse de votre stratégique post-signature</p>
                                        {message && (
                                            <p className="text-base font-bold text-blue-400 mt-1">{message}</p>
                                        )}
                                        {validationStatus && (
                                            <p className="text-base font-bold text-blue-400 mt-1">{validationStatus}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8 flex gap-4">
                                <Boutton
                                    text="VALIDER"
                                    onClick={handleValidateTurn}
                                    text_after="+2 ANS"
                                    disabled={isAnimating || barre}
                                    delay={0}
                                    variant="green"
                                />
                                <Boutton
                                    text="REFUSER"
                                    onClick={resetSignature}
                                    text_after="RETOUR"
                                    delay={0}
                                    variant="red"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

export default Decret;